import GUI from 'lil-gui'
import { autorun } from 'mobx'
import { useEffect, useRef } from 'react'
import { useRunState } from '../provider/useRunState'
import {
    getLaneCount,
    maxLifes,
} from '../config/constants'
import { store} from '@/src/stores/store'
import { powerUpPositions } from '../config/powerUpPositions'
import type { RunPhase } from '@/src/stores/runStateTypes'
import { useTrackController } from '../models/track/useTrackController'
import { obstaclePositions } from '../config/obstaclePositions'
import {
    footstepEffectConfigs,
    footstepEffectTypeKeys,
    type FootstepEffectType,
} from '../effects/footstepConfig'
import { getLevelProgress } from '../config/levels'
import { SHIELD_BREAK_EVENT } from '../models/character/shieldBreakEvent'
import { END_CARD_SPARK_REVEAL_EVENT } from '../models/card/endCardSparkBurstEvent'
import { dispatchConfettiBurst } from '@/src/components/ui/effects/confettiBurstEvent'

const HIGH_FIVE_STAR_EFFECT_START_EVENT = 'highfive-star-effect-start'
const HIGH_FIVE_STAR_EFFECT_END_EVENT = 'highfive-star-effect-end'
type UseGameDebugGuiOptions = {
    enabled: boolean
}

type DebugBindings = {
    phase: RunPhase
    lane: number
    distance: number
    lifes: number
    shieldActive: boolean
    invincible: boolean
    freeFlyCameraEnabled: boolean
    showObstacleHitboxes: boolean
    footstepEffect: FootstepEffectType
    trackSpeedInfo: number
    targetDistanceInfo: number
    obstacleCountInfo: number
    powerUpCountInfo: number

    startRunning: () => void
    freezeRun: () => void
    skipLevel: () => void
    jump: () => void
    activateHeart: () => void
    activateCoin: () => void
    activateBattery: () => void
    activatePlane: () => void
    activateShield: () => void
    forceGameOver: () => void
    forceLevelComplete: () => void
    resetGame: () => void
    triggerLevelUp: () => void
    triggerShieldBreakEffect: () => void
    triggerEndCardSparkBurstEffect: () => void
    triggerConfettiBurstEffect: () => void
}

type DebugController = {
    updateDisplay: () => void
}

export function useGameDebugGui({ enabled }: UseGameDebugGuiOptions) {
    const runState = useRunState()
    const trackController = useTrackController()

    const guiRef = useRef<GUI | null>(null)
    const bindingsRef = useRef<DebugBindings | null>(null)
    const controllersRef = useRef<DebugController[]>([])

    useEffect(() => {
        if (!enabled) {
            guiRef.current?.destroy()
            guiRef.current = null
            bindingsRef.current = null
            controllersRef.current = []
            return
        }

        if (guiRef.current) return

        const snapshot = runState.snapshot
        const trackPlayback = trackController.getTrackPlaybackState()
        const trackLength = trackController.getTrackLength()

        const bindings: DebugBindings = {
            phase: runState.phase,
            lane: snapshot.laneIndex,
            distance: trackPlayback.trackPosition,
            lifes: snapshot.lifes,
            shieldActive: snapshot.shieldActive,
            invincible: store.debugInvincible,
            freeFlyCameraEnabled: store.debugFreeFlyCameraEnabled,
            showObstacleHitboxes: store.debugShowObstacleHitboxes,
            footstepEffect: store.footstepEffect,
            trackSpeedInfo: trackPlayback.speed,
            targetDistanceInfo: trackLength,
            obstacleCountInfo: obstaclePositions.length,
            powerUpCountInfo: powerUpPositions.length,

            // Resume gameplay after a freeze or an in-game pause.
            startRunning: () => {
                trackController.setTrackPaused(false)
                runState.setPhase('running')
            },
            // Halt forward movement (speed 0) without leaving the run; resumed via Start Run.
            freezeRun: () => {
                trackController.setTrackPaused(true)
            },
            // Teleport to the next tunnel (region transition) and keep running.
            skipLevel: () => {
                const regionConfig = trackController.getResolvedTrackRegionConfig()
                const tunnelStarts = [
                    regionConfig.transition1.start,
                    regionConfig.transition2.start,
                    regionConfig.transition3.start,
                ]
                const currentPosition =
                    trackController.getTrackPlaybackState().trackPosition
                const nextTunnelStart = tunnelStarts.find(
                    (start) => start > currentPosition + 0.001,
                )
                const targetPosition =
                    nextTunnelStart ?? trackController.getTrackLength()

                trackController.setTrackPosition(targetPosition)
                trackController.setTrackPaused(false)
                runState.setTrackRegionState(
                    trackController.getTrackRegionState(targetPosition),
                )
                runState.setPhase('running')
            },
            jump: () => runState.startJump(),
            activateHeart: () => runState.activatePowerUp('heart'),
            activateCoin: () => runState.activatePowerUp('coin'),
            activateBattery: () => runState.activatePowerUp('battery'),
            activatePlane: () => runState.activatePowerUp('plane'),
            activateShield: () => runState.activatePowerUp('shield'),
            // Trigger the game-over screen with all required run state.
            forceGameOver: () => {
                trackController.setTrackPaused(true)
                runState.debugForceGameOver()
            },
            // Run the real win sequence to the end of the game → game-end screen.
            forceLevelComplete: () => {
                trackController.setTrackPosition(trackLength)
                trackController.setTrackPaused(false)
                runState.debugForceLevelComplete()
            },
            // Fully reload the app back to the loading screen.
            resetGame: () => {
                window.location.reload()
            },
            triggerLevelUp: () => {
                const currentLevel = getLevelProgress(Math.floor(runState.points)).level
                const nextLevel = Math.min(currentLevel + 1, 4)
                window.dispatchEvent(new CustomEvent('upgrade-unlock', {
                    detail: { unlockedCount: nextLevel },
                }))
            },
            triggerShieldBreakEffect: () => {
                window.dispatchEvent(new Event(SHIELD_BREAK_EVENT))
            },
            triggerEndCardSparkBurstEffect: () => {
                window.dispatchEvent(new Event(END_CARD_SPARK_REVEAL_EVENT))
            },
            triggerConfettiBurstEffect: () => {
                dispatchConfettiBurst()
            },
        }

        bindingsRef.current = bindings

        const gui = new GUI({ title: 'Game Debug' })
        gui.close()
        guiRef.current = gui

        const remember = (controller: DebugController) => {
            controllersRef.current.push(controller)
            return controller
        }

        const gameplayFolder = gui.addFolder('Gameplay')

        remember(
            gameplayFolder
                .add(bindings, 'phase', [
                    'idle',
                    'running',
                    'levelComplete',
                    'endCard',
                    'gameOver',
                    'paused',
                ])
                .name('Phase')
                .onChange((phase: RunPhase) => {
                    if (phase === 'running') {
                        trackController.setTrackPaused(false)
                    } else if (phase === 'paused') {
                        trackController.setTrackPaused(true)
                    }

                    if (phase === 'idle') {
                        trackController.restartTrack()
                    }

                    runState.debugSetPhase(phase)
                }),
        )

        remember(
            gameplayFolder
                .add(bindings, 'distance', 0, trackLength, 1)
                .name('Track Position')
                .onChange((distance: number) => {
                    trackController.setTrackPosition(distance)
                    runState.setTrackRegionState(
                        trackController.getTrackRegionState(distance),
                    )

                    if (runState.phase === 'idle') {
                        trackController.setTrackPaused(true)
                        runState.debugSetPhase('paused')
                    }
                }),
        )

        gameplayFolder.add(bindings, 'startRunning').name('Start/Resume Run')
        gameplayFolder.add(bindings, 'freezeRun').name('Freeze Run')
        gameplayFolder.add(bindings, 'skipLevel').name('Skip Level')
        gameplayFolder.add(bindings, 'forceGameOver').name('Game Over')
        gameplayFolder.add(bindings, 'forceLevelComplete').name('Game Complete')
        gameplayFolder.add(bindings, 'resetGame').name('Reset Game')
        gameplayFolder.close()

        const cameraFolder = gui.addFolder('Camera')
        remember(
            cameraFolder
                .add(bindings, 'freeFlyCameraEnabled')
                .name('Free Fly')
                .onChange((value: boolean) => {
                    store.setDebugFreeFlyCameraEnabled(value)
                }),
        )
        cameraFolder.close()

        const characterFolder = gui.addFolder('Character')

        remember(
            characterFolder
                .add(bindings, 'lane', 0, getLaneCount() - 1, 1)
                .name('Lane')
                .onChange((lane: number) => {
                    runState.setLaneIndex(lane)
                }),
        )

        remember(
            characterFolder
                .add(bindings, 'lifes', 0, maxLifes, 1)
                .name('Lifes')
                .onChange((lifes: number) => {
                    runState.setLifes(lifes)
                }),
        )

        remember(
            characterFolder
                .add(bindings, 'shieldActive')
                .name('Shield')
                .onChange((active: boolean) => {
                    runState.setShieldActive(active)
                }),
        )

        characterFolder.add(bindings, 'jump').name('Jump')
        characterFolder.add(bindings, 'triggerLevelUp').name('Trigger Level Up')
        characterFolder.close()

        const powerUpFolder = gui.addFolder('PowerUps')
        powerUpFolder.add(bindings, 'activateHeart').name('Heart')
        powerUpFolder.add(bindings, 'activateCoin').name('Coin')
        powerUpFolder.add(bindings, 'activateBattery').name('Battery')
        powerUpFolder.add(bindings, 'activatePlane').name('Star / Plane')
        powerUpFolder.add(bindings, 'activateShield').name('Shield')
        powerUpFolder.close()

        const effectsFolder = gui.addFolder('Effects')
        const footstepEffectOptions = Object.fromEntries(
            footstepEffectTypeKeys.map((key) => [footstepEffectConfigs[key].label, key]),
        ) as Record<string, FootstepEffectType>
        remember(
            effectsFolder
                .add(bindings, 'footstepEffect', footstepEffectOptions)
                .name('Footstep Effect')
                .onChange((effect: FootstepEffectType) => {
                    store.footstepEffect = effect
                }),
        )
        effectsFolder.add(bindings, 'triggerConfettiBurstEffect').name('Confetti Burst')
        effectsFolder.close()

        const debugViewFolder = gui.addFolder('Obstacles')
        remember(
            debugViewFolder
                .add(bindings, 'invincible')
                .name('Invincible')
                .onChange((active: boolean) => {
                    store.setDebugInvincible(active)
                }),
        )
        remember(
            debugViewFolder
                .add(bindings, 'showObstacleHitboxes')
                .name('Show Hitboxes')
                .onChange((show: boolean) => {
                    store.setDebugShowObstacleHitboxes(show)
                }),
        )
        debugViewFolder.close()

        const infoFolder = gui.addFolder('Info')
        remember(infoFolder.add(bindings, 'trackSpeedInfo').name('Track Speed').disable())
        remember(infoFolder.add(bindings, 'targetDistanceInfo').name('Target Distance').disable())
        remember(infoFolder.add(bindings, 'obstacleCountInfo').name('Obstacles').disable())
        remember(infoFolder.add(bindings, 'powerUpCountInfo').name('PowerUps').disable())
        infoFolder.close()

        return () => {
            gui.destroy()
            guiRef.current = null
            bindingsRef.current = null
            controllersRef.current = []
        }
    }, [enabled, runState, trackController])

    useEffect(() => {
        if (!enabled) return

        const syncBindings = () => {
            const bindings = bindingsRef.current

            if (!bindings) return

            const snapshot = runState.snapshot
            const trackPlayback = trackController.getTrackPlaybackState()

            bindings.phase = runState.phase
            bindings.lane = snapshot.laneIndex
            bindings.distance = trackPlayback.trackPosition
            bindings.lifes = snapshot.lifes
            bindings.shieldActive = snapshot.shieldActive
            bindings.invincible = store.debugInvincible
            bindings.freeFlyCameraEnabled = store.debugFreeFlyCameraEnabled
            bindings.showObstacleHitboxes = store.debugShowObstacleHitboxes
            bindings.footstepEffect = store.footstepEffect
            bindings.trackSpeedInfo = trackPlayback.speed
            bindings.targetDistanceInfo = trackController.getTrackLength()
            bindings.obstacleCountInfo = obstaclePositions.length
            bindings.powerUpCountInfo = powerUpPositions.length

            for (const controller of controllersRef.current) {
                controller.updateDisplay()
            }
        }

        syncBindings()

        const disposeAutorun = autorun(syncBindings)
        const unsubscribeTrack = trackController.subscribeTrackPlayback(() => {
            syncBindings()
        })

        return () => {
            disposeAutorun()
            unsubscribeTrack()
        }
    }, [enabled, runState, trackController])
}






